import {
  GetAccount200Response,
  GetAccount200ResponsePositionsInner,
} from "../../codegen-api";
import { dec } from "../../utils/amount/decimal";
import {
  isolatedAddMax,
  isolatedRemoveMax,
} from "../../utils/amount/max/isolatedMargin";
import { MaxResult } from "../../utils/amount/max/types";
import { apiNum } from "../../utils/math";

export interface MarginLimits {
  /** Collateral that can be added as isolated margin, as the P2 resolver sizes it. */
  addMax: MaxResult;
  /** Isolated margin removable while the position keeps its INITIAL margin. */
  removeMax: MaxResult;
  /** The free collateral figure shown in the details; `undefined` when unknown. */
  marginBalance: number | undefined;
}

type MarginPosition = Partial<
  Pick<
    GetAccount200ResponsePositionsInner,
    | "isolated_margin"
    | "maintenance_margin"
    | "amount"
    | "mark_price"
    | "unrealized_pnl"
  >
> & { leverage?: string | number | null };
type MarginAccount = Partial<
  Pick<GetAccount200Response, "available_balance" | "equity" | "initial_margin">
>;

const text = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

/**
 * What the adjust-isolated-margin form may add or remove.
 *
 * Remove keeps the position's INITIAL margin (decision 4): isolated margin,
 * less any unrealized loss, less notional ÷ leverage. The old rule kept only
 * maintenance margin, so Max stripped a position to the edge of liquidation.
 * Every term must be known — the backend sends `null` for an unpriced market,
 * and `Number(null)` is 0 — or the limit is unknown and Max says why.
 *
 * `accountLeverage` is the account's setting for this market, used when the
 * position payload carries no leverage of its own.
 */
export const computeMarginLimits = (
  position: MarginPosition | undefined,
  account: MarginAccount | undefined,
  accountLeverage: number | string | undefined,
  usdcDecimals: number
): MarginLimits => {
  // Free collateral from the API first; the equity-derived fallback needs
  // every term.
  let marginBalance = apiNum(account?.available_balance);
  let balanceText = text(account?.available_balance);
  if (marginBalance === undefined) {
    const equity = apiNum(account?.equity);
    const initial = apiNum(account?.initial_margin);
    const isolated = apiNum(position?.isolated_margin);
    const maintenance = apiNum(position?.maintenance_margin);
    marginBalance =
      equity !== undefined &&
      isolated !== undefined &&
      maintenance !== undefined &&
      initial !== undefined
        ? equity - isolated - maintenance - initial
        : undefined;
    balanceText = marginBalance === undefined ? null : dec.parse(marginBalance);
  }

  const addMax = isolatedAddMax({ availableBalance: balanceText, usdcDecimals });
  const removeMax = position
    ? isolatedRemoveMax({
        isolatedMargin: text(position.isolated_margin),
        positionAmount: text(position.amount),
        markPrice: text(position.mark_price),
        leverage: position.leverage ?? accountLeverage ?? null,
        unrealizedPnl: text(position.unrealized_pnl),
        usdcDecimals,
      })
    : ({ ok: false, reason: "no_position" } as MaxResult);

  return { addMax, removeMax, marginBalance };
};

export type MarginAmountIssue =
  | "insufficient_balance"
  | "balance_unknown"
  | "below_initial_margin"
  | "remove_unknown"
  | undefined;

/**
 * Why an entered amount cannot be submitted, compared exactly. An unknown
 * limit blocks rather than passes: the form cannot check the amount, so it
 * must not claim it is fine.
 */
export const marginAmountIssue = (
  amount: string | undefined,
  isAddMargin: boolean,
  limits: Pick<MarginLimits, "addMax" | "removeMax">
): MarginAmountIssue => {
  const value = dec.parse(amount ?? null);
  if (value === null || !dec.gt(value, "0")) return undefined;
  if (isAddMargin) {
    if (!limits.addMax.ok) return "balance_unknown";
    return dec.gt(value, limits.addMax.max) ? "insufficient_balance" : undefined;
  }
  if (!limits.removeMax.ok) return "remove_unknown";
  return dec.gt(value, limits.removeMax.max) ? "below_initial_margin" : undefined;
};
